/**
 * Report Generation Service
 *
 * Calls the analyst-api proxy that forwards to func-axn-operations-api's
 * /api/reports/generate. Synchronous — expected to take 30-60s.
 */

import { apiFetch, apiPost, buildQueryString, type ApiResponse } from './api';

// ============================================================================
// Types
// ============================================================================

/**
 * One analyst-curated featured incident. Array ORDER is the display order
 * (index 0 first). narrative/lessons are used verbatim in the report — the
 * writing agent does not rewrite them.
 */
export interface FeaturedIncidentInput {
  incidentId: string;
  title?: string;
  whyItMattered?: string;
  narrative?: string[];
  lessons?: string[];
}

// Analyst-authored "what we did this month" — entered during creation, rendered
// verbatim (the writing agent never touches it).
export interface ServiceDeliveryHunts {
  count?: number;
  summary?: string;
  resultingAnalytics: string[];
}
export interface ServiceDeliveryCloudApp {
  name: string;
  hosting?: string;
  dataCenter?: string;
  auth?: string;
  complianceScore?: string;
  usage?: string;
  traffic?: string;
}
export interface ServiceDelivery {
  solutionsUpdated: string[];
  solutionsInstalled: string[];
  analyticsUpdated: string[];
  analyticsEnabled: string[];
  hunts: ServiceDeliveryHunts;
  cloudAppDiscovery: ServiceDeliveryCloudApp[];
  /** Free-form "other work performed" catch-all — markdown. */
  otherWork?: string;
}

export interface GenerateReportRequest {
  tenantId: string;
  periodStart: string; // ISO
  periodEnd: string;   // ISO
  issueNumber?: number;
  sections?: Partial<Record<
    'cover' | 'spread' | 'statCards' | 'feature' | 'retroCards' | 'industry' | 'colophon',
    boolean
  >>;
  /** Analyst-curated featured incidents, ordered, with approved narration. */
  featuredIncidents?: FeaturedIncidentInput[];
  /** Analyst-authored "what we did this month" content. */
  serviceDelivery?: ServiceDelivery;
  retroIncidentIds?: string[];
  industryUpdateIds?: string[];
  overrides?: Record<string, unknown>;
  customInstructions?: string;
}

/** A candidate/suggestion row for the report-build curate screen. */
export interface ReportCandidate {
  incidentId: string;
  title: string;
  severity: string;
  incidentFamily: string | null;
  riskScore: number | null;
  status: string;
  createdAt: string;
  summary: string | null;
  reasoning: string | null;
  immediateAction: string | null;
  reportCandidate: boolean;
  reportCandidateBy: string | null;
  reportCandidateAt: string | null;
}

export interface ReportCandidatesResponse {
  tenantId: string;
  period: { start: string; end: string };
  candidates: ReportCandidate[];
  suggestions: ReportCandidate[];
}

/** Per-incident narration draft from the writing agent. */
export interface IncidentNarration {
  whyItMattered: string;
  narrative: string[];
  lessons: string[];
}

export interface IncidentNarrationResponse {
  incidentId: string;
  narration: IncidentNarration;
  runId: string;
  tokenCount: number | null;
}

// Mirrors func-axn-operations-api/src/schemas/authoredSections.ts — kept
// loose here so the form can render whatever the agent returns without
// getting in the way of iteration.
export interface AuthoredSections {
  issueNumber?: number;
  cover?: unknown;
  spread?: unknown;
  statCards?: unknown[];
  featureIntro?: string;
  chartCaptions?: Record<string, string>;
  retroCards?: unknown[];
  industryCards?: unknown[];
  colophon?: unknown;
  [key: string]: unknown;
}

export interface GenerateReportResponse {
  reportId: string;
  runId: string;
  issueNumber: number;
  reportTitle: string;
  tenantId: string;
  periodStart: string;
  periodEnd: string;
  authoredSections: AuthoredSections;
  structuredData: Record<string, unknown>;
  tokenCount: number | null;
  durationMs: number;
}

// ============================================================================
// API
// ============================================================================

export async function generateReport(
  body: GenerateReportRequest,
): Promise<ApiResponse<GenerateReportResponse>> {
  return apiPost<GenerateReportResponse>('/reports/generate', body);
}

/**
 * Candidates + auto-suggestions for the curate screen (analyst-marked
 * incidents for the period, plus the auto top-5 the report would otherwise use).
 */
export async function getReportCandidates(
  tenantId: string,
  periodStart: string,
  periodEnd: string,
): Promise<ApiResponse<ReportCandidatesResponse>> {
  const qs = buildQueryString({ periodStart, periodEnd });
  return apiFetch<ReportCandidatesResponse>(
    `/reports/${encodeURIComponent(tenantId)}/candidates${qs}`,
  );
}

/**
 * Draft the "why this mattered" narration for one featured incident. Returns
 * { whyItMattered, narrative[], lessons[] } for the analyst to edit/approve.
 * Spins up a writing-agent thread — expect a few seconds.
 */
export async function draftIncidentNarration(
  tenantId: string,
  incidentId: string,
): Promise<ApiResponse<IncidentNarrationResponse>> {
  return apiPost<IncidentNarrationResponse>('/reports/incident-narration', {
    tenantId,
    incidentId,
  });
}
